import React, { useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { PaperPlaneTilt, CircleNotch, CheckCircle, WarningCircle, UserCircle } from '@phosphor-icons/react';
import { useAuthOptional } from '../contexts/AuthContext';
import { trackEvent } from '../utils/analytics/analytics';
import { Msg, useFormatMessage } from '../i18n-review/client/Msg';

export interface FeedbackFormSuccessDetail {
  authenticated: boolean;
}

interface FeedbackFormProps {
  source?: string;
  metadata?: Record<string, unknown>;
  onSuccess?: (detail?: FeedbackFormSuccessDetail) => void;
  onCancel?: () => void;
  className?: string;
}

export function FeedbackForm({ source = 'general', metadata, onSuccess, onCancel, className = '' }: FeedbackFormProps) {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const auth = useAuthOptional();
  const isAuthenticated = auth?.isAuthenticated ?? false;
  const signIn = auth?.signIn;
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedback.trim()) return;

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage(null);

    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          message: feedback.trim(),
          source,
          metadata,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to submit feedback');
      }

      setSubmitStatus('success');
      setFeedback('');
      trackEvent({ name: 'feedback_submit', params: { locale: intl.locale } });

      if (onSuccess) {
        // When AuthProvider is missing, treat as authenticated so modals don't stay open unnecessarily.
        onSuccess({ authenticated: auth ? isAuthenticated : true });
      }

      // Reset success message after 3 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 3000);
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'An error occurred');
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Allow anonymous feedback - no authentication required

  return (
    <form onSubmit={handleSubmit} className={`space-y-5 ${className}`}>
      <div>
        <label htmlFor="feedback-message" className="block text-sm font-normal text-jc-ink-secondary mb-3">
          <Msg id="feedback.label" defaultMessage="Your Feedback" description="Label for feedback textarea" />
        </label>
        <textarea
          id="feedback-message"
          className="w-full px-4 py-3 border border-jc-border/80 rounded-2xl bg-jc-page/80 text-jc-ink placeholder-jc-ink-secondary focus:outline-none focus:ring-2 focus:ring-jc-accent/40 focus:border-jc-accent/50 resize-none transition-all duration-(--jc-duration-base) cursor-text"
          rows={5}
          placeholder={formatMessage({
            id: 'feedback.placeholder',
            defaultMessage: 'Tell us what we should change or add...',
            description: 'Placeholder text for feedback textarea',
          })}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          disabled={isSubmitting}
          required
        />
      </div>

      {submitStatus === 'success' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-jc-accent text-sm px-4 py-3 bg-jc-accent-muted/50 rounded-2xl border border-jc-accent/20">
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            <Msg
              id="feedback.success"
              defaultMessage="Thank you! Your feedback has been submitted."
              description="Success message after submitting feedback"
            />
          </div>
          {!isAuthenticated && signIn ? (
            <div className="rounded-2xl border border-jc-border bg-jc-page/80 px-4 py-4 space-y-3">
              <p className="text-sm font-semibold text-jc-ink">
                <Msg
                  id="feedback.postSubmit.signInTitle"
                  defaultMessage="Want us to follow up?"
                  description="Heading after anonymous feedback submit"
                />
              </p>
              <p className="text-xs text-jc-ink-secondary leading-relaxed">
                <Msg
                  id="feedback.postSubmit.signInBody"
                  defaultMessage="Activate your account so we can reply if we have questions about your feedback."
                  description="Body after anonymous feedback submit encouraging sign-in"
                />
              </p>
              <button
                type="button"
                onClick={() => {
                  trackEvent({
                    name: 'auth_interaction',
                    params: { action: 'sign_in_click', surface: 'feedback_post_submit', locale: intl.locale },
                  });
                  signIn();
                }}
                className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-jc-cta px-4 py-2.5 text-sm font-semibold text-jc-on-cta shadow-jc-sm transition hover:bg-jc-action-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-jc-work-remote cursor-pointer"
              >
                <UserCircle className="h-4 w-4 shrink-0" />
                <Msg
                  id="feedback.postSubmit.signInButton"
                  defaultMessage="Sign in or activate account"
                  description="Button after feedback submit to sign in"
                />
              </button>
            </div>
          ) : null}
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="flex items-center gap-2 text-jc-ink text-sm px-4 py-3 bg-jc-elevated rounded-2xl">
          <WarningCircle className="w-4 h-4 flex-shrink-0" />
          <span>
            {errorMessage ||
              formatMessage({
                id: 'feedback.error',
                defaultMessage: 'Failed to submit feedback. Please try again.',
                description: 'Error message when feedback submission fails',
              })}
          </span>
        </div>
      )}

      <div className="flex items-center justify-end gap-3 pt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-5 py-2.5 text-sm font-semibold text-jc-ink-secondary hover:bg-jc-elevated rounded-2xl transition-colors duration-(--jc-duration-base) focus:outline-none focus:ring-2 focus:ring-jc-accent/30 cursor-pointer"
          >
            <Msg id="feedback.cancel" defaultMessage="Cancel" description="Cancel button text" />
          </button>
        )}
        <button
          type="submit"
          disabled={!feedback.trim() || isSubmitting}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-jc-cta hover:bg-jc-cta-hover disabled:bg-jc-border disabled:cursor-not-allowed text-jc-on-cta text-sm font-semibold rounded-2xl transition-all duration-(--jc-duration-base) focus:outline-none focus:ring-2 focus:ring-jc-work-remote/50 focus:ring-offset-2 focus:ring-offset-jc-page shadow-jc-sm hover:shadow-jc-md dark:hover:shadow-none disabled:shadow-none cursor-pointer"
        >
          {isSubmitting ? (
            <>
              <CircleNotch className="w-4 h-4 animate-spin" />
              <Msg
                id="feedback.submitting"
                defaultMessage="Submitting..."
                description="Button text when submitting feedback"
              />
            </>
          ) : (
            <>
              <PaperPlaneTilt className="w-4 h-4" />
              <Msg id="feedback.submit" defaultMessage="Submit" description="Button text for submitting feedback" />
            </>
          )}
        </button>
      </div>
    </form>
  );
}
